// src/PasteData.js
import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Alert,
  Collapse,
  IconButton,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

function PasteData({ setFlights, breakdownParams }) {
  const navigate = useNavigate();

  // Flight Number + raw text states
  const [flightNumber, setFlightNumber] = useState('');
  const [tdrText, setTdrText] = useState('');
  const [brockText, setBrockText] = useState('');
  // Add loading and general error states
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [showGeneralError, setShowGeneralError] = useState(false);

  // ---------------------------
  // TDR mismatch / success states
  // ---------------------------
  const [tdrErrorMessage, setTdrErrorMessage] = useState('');
  const [tdrOpenError, setTdrOpenError] = useState(false);
  const [tdrSuccessMessage, setTdrSuccessMessage] = useState('');
  const [tdrOpenSuccess, setTdrOpenSuccess] = useState(false);
  const [flightNumberTdrError, setFlightNumberTdrError] = useState(false);
  const [tdrFieldError, setTdrFieldError] = useState(false);
  const [flightNumberTdrSuccess, setFlightNumberTdrSuccess] = useState(false);
  const [tdrFieldSuccess, setTdrFieldSuccess] = useState(false);

  // ---------------------------
  // Brock mismatch / success states
  // ---------------------------
  const [brockErrorMessage, setBrockErrorMessage] = useState('');
  const [brockOpenError, setBrockOpenError] = useState(false);
  const [brockSuccessMessage, setBrockSuccessMessage] = useState('');
  const [brockOpenSuccess, setBrockOpenSuccess] = useState(false);
  const [flightNumberBrockError, setFlightNumberBrockError] = useState(false);
  const [brockFieldError, setBrockFieldError] = useState(false);
  const [flightNumberBrockSuccess, setFlightNumberBrockSuccess] = useState(false);
  const [brockFieldSuccess, setBrockFieldSuccess] = useState(false);

  // ---------------------------
  // Local Parsers
  // ---------------------------
  const parseTdrFlightNumber = (text) => {
    // For TDR lines like "Flight: 3820"
    const match = text.match(/Flight:\s*([A-Za-z0-9]+)/);
    return match ? match[1] : null;
  };
  
  const parseBrockFlightNumber = (text) => {
    // For Brock lines like "WN 3820" or "WN 0777" with leading zero
    const match = text.match(/WN\s+0?(\d+)/);
    return match ? match[1] : null;
  };

  // ---------------------------
  // Clear TDR states
  // ---------------------------
  const clearTdrError = () => {
    setTdrErrorMessage('');
    setTdrOpenError(false);
    setFlightNumberTdrError(false);
    setTdrFieldError(false);
  };
  const clearTdrSuccess = () => {
    setTdrSuccessMessage('');
    setTdrOpenSuccess(false);
    setFlightNumberTdrSuccess(false);
    setTdrFieldSuccess(false);
  };
  const clearAllTdrStates = () => {
    clearTdrError();
    clearTdrSuccess();
  };

  // ---------------------------
  // Clear Brock states
  // ---------------------------
  const clearBrockError = () => {
    setBrockErrorMessage('');
    setBrockOpenError(false);
    setFlightNumberBrockError(false);
    setBrockFieldError(false);
  };
  const clearBrockSuccess = () => {
    setBrockSuccessMessage('');
    setBrockOpenSuccess(false);
    setFlightNumberBrockSuccess(false);
    setBrockFieldSuccess(false);
  };
  const clearAllBrockStates = () => {
    clearBrockError();
    clearBrockSuccess();
  };

  // ---------------------------
  // Checking TDR flight vs typed flight
  // ---------------------------
  const checkTdrMatch = (tdrFltNum, typedFltNum) => {
    if (!tdrFltNum || !typedFltNum) {
      clearAllTdrStates();
      return;
    }
    if (tdrFltNum === typedFltNum) {
      // MATCH
      clearTdrError();
      setTdrSuccessMessage(`TDR Flight Number (${tdrFltNum}) matches Flight Number (${typedFltNum}).`);
      setTdrOpenSuccess(true);
      setFlightNumberTdrSuccess(true);
      setTdrFieldSuccess(true);
    } else {
      // MISMATCH
      clearTdrSuccess();
      setTdrErrorMessage(
        `TDR Flight Number (${tdrFltNum}) does not match Flight Number (${typedFltNum}).`
      );
      setTdrOpenError(true);
      setFlightNumberTdrError(true);
      setTdrFieldError(true);
    }
  };

  // ---------------------------
  // Checking Brock flight vs typed flight
  // ---------------------------
  const checkBrockMatch = (brockFltNum, typedFltNum) => {
    if (!brockFltNum || !typedFltNum) {
      clearAllBrockStates();
      return;
    }
    if (brockFltNum === typedFltNum) {
      // MATCH
      clearBrockError();
      setBrockSuccessMessage(
        `Brock / SmartSuite Flight Number (${brockFltNum}) matches Flight Number (${typedFltNum}).`
      );
      setBrockOpenSuccess(true);
      setFlightNumberBrockSuccess(true);
      setBrockFieldSuccess(true);
    } else {
      // MISMATCH
      clearBrockSuccess();
      setBrockErrorMessage(
        `Brock / SmartSuite Flight Number (${brockFltNum}) does not match Flight Number (${typedFltNum}).`
      );
      setBrockOpenError(true);
      setFlightNumberBrockError(true);
      setBrockFieldError(true);
    }
  };

  // ---------------------------
  // onChange Handlers
  // ---------------------------
  const handleFlightNumberChange = (e) => {
    const newFlightNum = e.target.value;
    setFlightNumber(newFlightNum);

    // Re-check TDR
    const tdrFltNum = parseTdrFlightNumber(tdrText);
    checkTdrMatch(tdrFltNum, newFlightNum);

    // Re-check Brock
    const brockFltNum = parseBrockFlightNumber(brockText);
    checkBrockMatch(brockFltNum, newFlightNum);
  };

  const handleTdrChange = (e) => {
    const newTdr = e.target.value;
    setTdrText(newTdr);

    const tdrFltNum = parseTdrFlightNumber(newTdr);
    checkTdrMatch(tdrFltNum, flightNumber);
  };

  const handleBrockChange = (e) => {
    const newBrock = e.target.value;
    setBrockText(newBrock);

    const brockFltNum = parseBrockFlightNumber(newBrock);
    checkBrockMatch(brockFltNum, flightNumber);
  };

  // ---------------------------
  // Dynamic styling for TDR fields
  // ---------------------------
  const getTdrBorderColor = () => {
    if (tdrFieldError) return 'red';
    if (tdrFieldSuccess) return 'green';
    return '#e2e2e2';
  };
  const getTdrLabelColor = () => {
    if (tdrFieldError) return 'red';
    if (tdrFieldSuccess) return 'green';
    return '#e2e2e2';
  };
  const getTdrFlightNumBorderColor = () => {
    if (flightNumberTdrError) return 'red';
    if (flightNumberTdrSuccess) return 'green';
    return '#e2e2e2';
  };
  const getTdrFlightNumLabelColor = () => {
    if (flightNumberTdrError) return 'red';
    if (flightNumberTdrSuccess) return 'green';
    return '#e2e2e2';
  };

  // ---------------------------
  // Dynamic styling for Brock fields
  // ---------------------------
  const getBrockBorderColor = () => {
    if (brockFieldError) return 'red';
    if (brockFieldSuccess) return 'green';
    return '#e2e2e2';
  };
  const getBrockLabelColor = () => {
    if (brockFieldError) return 'red';
    if (brockFieldSuccess) return 'green';
    return '#e2e2e2';
  };
  const getBrockFlightNumBorderColor = () => {
    if (flightNumberBrockError) return 'red';
    if (flightNumberBrockSuccess) return 'green';
    return '#e2e2e2';
  };
  const getBrockFlightNumLabelColor = () => {
    if (flightNumberBrockError) return 'red';
    if (flightNumberBrockSuccess) return 'green';
    return '#e2e2e2';
  };

  // ---------------------------
  // "BREAKDOWN" logic
  // ---------------------------
  const handleSubmit = async () => {
    try {
      // Input validation
      if (!flightNumber.trim()) {
        setGeneralError('Please enter a flight number');
        setShowGeneralError(true);
        return;
      }
      
      if (!tdrText.trim()) {
        setGeneralError('Please paste TDR text');
        setShowGeneralError(true);
        return;
      }
      
      if (!brockText.trim()) {
        setGeneralError('Please paste Brock/SmartSuite text');
        setShowGeneralError(true);
        return;
      }
      
      // Clear any previous errors and set loading state
      setGeneralError('');
      setShowGeneralError(false);
      setLoading(true);
      
      // Log the parameters for debugging
      console.log("Submitting with parameters:", breakdownParams);
      console.log("Sending data to server:", {
        flightNumber,
        tdrText: tdrText.substring(0, 50) + "...", // Truncate for logging
        brockText: brockText.substring(0, 50) + "..." // Truncate for logging
      });
      
      // Make the API call
      const res = await axios.post('/paste-data', {
        flightNumber,
        tdrText,
        brockText
      });

      console.log("Received response:", res);
      
      // Expecting { tdrData: {...}, brockData: {...} }
      if (res.data && res.data.tdrData && res.data.brockData) {
        console.log("Valid data received from server");
        
        // 1) Store in parent state if needed
        if (setFlights) {
          setFlights(res.data);
        }

        // Create a clean copy of the parameters to ensure the boolean value is preserved
        const cleanParams = {
          isPriorityFlight: breakdownParams?.isPriorityFlight === true, // Explicitly convert to boolean
          hotThreshold: parseInt(breakdownParams?.hotThreshold || 30, 10),
          coldThresholdHours: parseFloat(breakdownParams?.coldThresholdHours || 3)
        };

        // 2) Store in localStorage to list in /flights - INCLUDING parameters
        const storedFlights = JSON.parse(localStorage.getItem('flightList') || '[]');
        storedFlights.push({
          tdrData: res.data.tdrData,
          brockData: res.data.brockData,
          breakdownParams: cleanParams, // Store parameters along with the flight data
          timestamp: Date.now()
        });
        localStorage.setItem('flightList', JSON.stringify(storedFlights));

        // Log the exact parameters being passed to navigation
        console.log("Navigating with cleaned parameters:", cleanParams);
        console.log("isPriorityFlight type:", typeof cleanParams.isPriorityFlight);

        // 3) Navigate to /flight-data with the parameters
        navigate('/flight-data', {
          state: {
            tdrData: res.data.tdrData,
            brockData: res.data.brockData,
            breakdownParams: cleanParams
          }
        });
      } else {
        console.error('Invalid response format from server', res.data);
        setGeneralError('Server returned an invalid response format. Check console for details.');
        setShowGeneralError(true);
      }
    } catch (err) {
      console.error('Error calling /paste-data:', err);
      
      let errorMessage = 'Error connecting to the server. Is your backend running?';
      
      if (err.response) {
        console.error('Server responded with:', err.response.data);
        if (err.response.data && err.response.data.error) {
          errorMessage = `Server error: ${err.response.data.error}`;
        } else {
          errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
        }
      } else if (err.request) {
        console.error('No response received from server');
        errorMessage = 'No response received from server. Check if your backend is running.';
      } else {
        console.error('Error setting up request:', err.message);
        errorMessage = `Request error: ${err.message}`;
      }
      
      setGeneralError(errorMessage);
      setShowGeneralError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ textAlign: 'left', marginBottom: '2rem' }}>
      {/* General Error Alert */}
      <Collapse in={showGeneralError} sx={{ marginBottom: '1rem' }}>
        <Alert
          severity="error"
          variant="filled"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setShowGeneralError(false)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {generalError}
        </Alert>
      </Collapse>
    
      {/* TDR Error Alert */}
      <Collapse in={tdrOpenError} sx={{ marginBottom: '1rem' }}>
        <Alert
          severity="error"
          variant="filled"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => clearTdrError()}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {tdrErrorMessage}
        </Alert>
      </Collapse>

      {/* TDR Success Alert */}
      <Collapse in={tdrOpenSuccess} sx={{ marginBottom: '1rem' }}>
        <Alert
          severity="success"
          variant="filled"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => clearTdrSuccess()}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {tdrSuccessMessage}
        </Alert>
      </Collapse>

      {/* Brock Error Alert */}
      <Collapse in={brockOpenError} sx={{ marginBottom: '1rem' }}>
        <Alert
          severity="error"
          variant="filled"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => clearBrockError()}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {brockErrorMessage}
        </Alert>
      </Collapse>

      {/* Brock Success Alert */}
      <Collapse in={brockOpenSuccess} sx={{ marginBottom: '1rem' }}>
        <Alert
          severity="success"
          variant="filled"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => clearBrockSuccess()}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {brockSuccessMessage}
        </Alert>
      </Collapse>

      <Typography variant="h6" sx={{ color: '#F9B612', marginBottom: '1rem' }}>
        Paste TDR and Brock / SmartSuite Data
      </Typography>

      {/* Flight Number */}
      <TextField
        label="Flight Number"
        value={flightNumber}
        onChange={handleFlightNumberChange}
        placeholder="Enter flight number (e.g., 3820)"
        variant="outlined"
        fullWidth
        InputLabelProps={{ shrink: true }}
        sx={{
          marginBottom: '1rem',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: getTdrFlightNumBorderColor() || getBrockFlightNumBorderColor(),
            },
            '&:hover fieldset': {
              borderColor: getTdrFlightNumBorderColor() || getBrockFlightNumBorderColor(),
            },
            '&.Mui-focused fieldset': {
              borderColor: getTdrFlightNumBorderColor() || getBrockFlightNumBorderColor(),
            },
          },
          '& .MuiInputBase-input': {
            color: '#e2e2e2',
          },
          '& .MuiInputLabel-root': {
            color: getTdrFlightNumLabelColor() || getBrockFlightNumLabelColor(),
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: getTdrFlightNumLabelColor() || getBrockFlightNumLabelColor(),
          },
        }}
      />

      {/* TDR Raw Text */}
      <TextField
        label="TDR"
        multiline
        rows={8}
        value={tdrText}
        onChange={handleTdrChange}
        placeholder="Paste the full TDR here. Include the print time..."
        variant="outlined"
        fullWidth
        InputLabelProps={{ shrink: true }}
        sx={{
          marginBottom: '1rem',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: getTdrBorderColor(),
            },
            '&:hover fieldset': {
              borderColor: getTdrBorderColor(),
            },
            '&.Mui-focused fieldset': {
              borderColor: getTdrBorderColor(),
            },
          },
          '& .MuiInputBase-input': {
            color: '#e2e2e2',
          },
          '& .MuiInputLabel-root': {
            color: getTdrLabelColor(),
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: getTdrLabelColor(),
          },
        }}
      />

      {/* Brock Raw Text */}
      <TextField
        label="Brock / SmartSuite"
        multiline
        rows={8}
        value={brockText}
        onChange={handleBrockChange}
        placeholder="Paste Brock / SmartSuite bin information here."
        variant="outlined"
        fullWidth
        InputLabelProps={{ shrink: true }}
        sx={{
          marginBottom: '1rem',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: getBrockBorderColor(),
            },
            '&:hover fieldset': {
              borderColor: getBrockBorderColor(),
            },
            '&.Mui-focused fieldset': {
              borderColor: getBrockBorderColor(),
            },
          },
          '& .MuiInputBase-input': {
            color: '#e2e2e2',
          },
          '& .MuiInputLabel-root': {
            color: getBrockLabelColor(),
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: getBrockLabelColor(),
          },
        }}
      />

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={loading}
        sx={{
          backgroundColor: '#e2e2e2',
          color: '#000',
          fontWeight: 'bold',
          width: '100%', // full width
          '&:hover': {
            backgroundColor: '#ccc',
          },
          '&.Mui-disabled': {
            backgroundColor: '#a0a0a0',
            color: '#333',
          },
        }}
      >
        {loading ? (
          <>
            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
            PROCESSING...
          </>
        ) : (
          'BREAKDOWN'
        )}
      </Button>
    </div>
  );
}

export default PasteData;